import React, { useState, Fragment } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Paper,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { StepProps } from '../../../components/_WMS/Stepper'
import { RelocateState } from '../types'
import { LoadingButton } from '../../../components/_EOS/Button/LoadingButton/LoadingButton'
import { formatSkuLabel } from '../utils/dunUtils'
import { ApiConfig } from '../../../config/api'
import { useApi } from '../../../hooks/useApi'
import axios from 'axios'
import { ErrorResponse } from '../../../interfaces'
import { getStepperContainerHeight } from '../../../utils/helpers'

interface ConfirmationStepProps extends StepProps<RelocateState> { }

// API function for executing relocation
const executeRelocateMovement = (data: {
  originPosition: {
    positionCode: string,
    zoneName: string | null
  },
  destinationPosition: {
    positionCode: string,
    zoneName: string | null
  },
  sku: string,
  stockKeepingUnitId: number,
  qty: number,
  documentNumber: string,
  observation: string,
  batchId?: number,
  // Traza del movimiento por DUN: `qty` sigue viajando en unidades base, estos
  // campos son los que permiten distinguir en el log "2 cajas de 6" de "12 sueltas".
  stockKeepingUnitDunId?: number,
  dun?: string,
  dunQty?: number
}) => {
  return axios.post(`${ApiConfig.RelocateApi}/RelocateSkuPositions`, data)
}

export const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  prev,
  sharedState,
  setSharedState,
  goToStep
}) => {
  const [isExecuting, setIsExecuting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState('')
  const [countdown, setCountdown] = useState(5)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { callApi } = useApi()

  // Con DUN se informan las dos magnitudes: las cajas que movió el operador y las
  // unidades base que realmente impactan el inventario.
  const cantidadLabel = sharedState.isDun
    ? `${sharedState.cantidadDun} ${sharedState.typePackageName || 'Piezas agrupadas'} (${sharedState.cantidad} unidades)`
    : `${sharedState.cantidad} unidades`

  // El código completo del DUN se muestra en las dos pantallas: en la confirmación
  // previa como último control antes de impactar el inventario, y en el recibo como
  // registro de lo que se hizo.
  const showDunCode = Boolean(sharedState.isDun)

  // Mismos chips que el header del paso 2: el operador reconoce la referencia que
  // viene siguiendo en lugar de tener que releerla en otro formato.
  const productChips = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        mb: 2
      }}
    >
      {sharedState.isGreenSale && (
        <Chip
          label='Venta en Verde'
          size='small'
          style={{
            backgroundColor: '#2e7d32',
            color: '#ffffff',
            fontWeight: 600,
            border: 'none'
          }}
        />
      )}
      <Chip
        label={`Marca: ${sharedState.productOriginName}`}
        color='primary'
        variant='outlined'
        size='small'
        style={{ fontWeight: 600, maxWidth: '100%' }}
      />
      <Chip
        label={`SKU: ${formatSkuLabel(sharedState)}`}
        color='primary'
        variant='outlined'
        size='small'
        style={{ fontWeight: 600, maxWidth: '100%' }}
      />
    </Box>
  )

  const handleExecuteRelocate = async () => {
    setIsExecuting(true)
    setError('')

    try {
      // Preparar datos para el API según el formato requerido
      const relocateData = {
        originPosition: {
          positionCode: sharedState.posicionOrigen,
          zoneName: null // No tenemos información de zona en el nuevo sistema por ahora
        },
        destinationPosition: {
          positionCode: sharedState.posicionDestino,
          zoneName: null // No tenemos información de zona en el nuevo sistema por ahora
        },
        sku: sharedState.sku,
        stockKeepingUnitId: sharedState.stockKeepingUnitId || 1,
        // Siempre unidades base: la conversión de cajas a unidades ya se hizo al
        // validar la cantidad (useQuantityValidation).
        qty: sharedState.cantidad || 0,
        documentNumber: sharedState.documentNumber || '',
        observation: sharedState.observations || '',
        ...(sharedState.selectedBatch && { batchId: sharedState.selectedBatch.batchId }),
        ...(sharedState.isDun && {
          stockKeepingUnitDunId: sharedState.stockKeepingUnitDunId,
          dun: sharedState.dunCode,
          dunQty: sharedState.dunQty
        })
      }

      // Ejecutar la reubicación
      await callApi(executeRelocateMovement(relocateData))

      // Mostrar pantalla de éxito
      setIsSuccess(true)

      // Iniciar countdown
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval)
            handleReturnToStart()
            return 0
          }
          return prev - 1
        })
      }, 1000)

    } catch (err) {
      // Si el err es un string, lo pasamos directamente
      if (typeof err === 'string') {
        setError(err)
      } else {
        // Si no, tratamos de mapearlo
        // Extraer el mensaje de error del backend
        const backendMessage = (err as ErrorResponse)?.message
        const genericMessage = (err as any)?.message

        // Usar el mensaje específico del backend si existe, sino usar mensaje genérico
        const errorMessage = backendMessage || genericMessage || 'Error al ejecutar la reubicación'

        setError(errorMessage)
      }

    } finally {
      setIsExecuting(false)
    }
  }


  const handleReturnToStart = () => {
    // Reset completo del estado para nueva reubicación
    setSharedState({
      sku: '',
      productOriginId: null,
      productOriginName: '',
      isGreenSale: false,
      posicionOrigen: '',
      cantidad: null,
      posicionDestino: '',
      isDamaged: false,
      availableStock: undefined,
      originPositionId: undefined,
      destinyPositionId: undefined,
      originPositionType: undefined,
      originZone: undefined,
      stockKeepingUnitId: undefined,
      availablePositions: undefined,
      suggestedPositions: undefined,
      hasBatchManagement: false,
      hasExpirationDate: false,
      selectedBatch: undefined,
      availableBatches: undefined,
      showBatchModal: false,
      isDun: false,
      dunCode: undefined,
      dunQty: undefined,
      stockKeepingUnitDunId: undefined,
      typePackageName: undefined,
      cantidadDun: null,
      documentNumber: undefined,
      observations: undefined,
      requiresComments: false,
      error: undefined,
      isLoading: false
    })

    // Reset estado local
    setIsSuccess(false)
    setCountdown(5)
    setError('')

    // Volver al primer paso
    goToStep(0)
  }

  const canExecute = !sharedState.requiresComments ||
    (sharedState.requiresComments && sharedState.documentNumber?.trim() && sharedState.observations?.trim())


  // Si es éxito, mostrar pantalla de recibo
  if (isSuccess) {
    return (
      <Box
        style={{
          height: getStepperContainerHeight(),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
          overflow: 'auto',
          padding: isMobile ? '8px 0' : '16px 0'
        }}
      >
        <Card
          style={{
            maxWidth: 600,
            width: '90%',
            padding: 24,
            textAlign: 'center',
            border: `2px solid ${theme.palette.success.main}`,
            borderRadius: 12
          }}
        >
          <CardContent>
            {/* Título de éxito */}
            <Typography variant="h4" style={{ color: theme.palette.success.main, marginBottom: 16, fontWeight: 'bold' }}>
              ✓ Reubicación Exitosa
            </Typography>

            <Divider style={{ margin: '20px 0' }} />

            {/* Información del producto */}
            {productChips}

            {/* Detalles del movimiento estilo recibo */}
            <Box style={{ textAlign: 'left', marginBottom: 20 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Origen:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {sharedState.posicionOrigen}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Destino:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {sharedState.posicionDestino}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Cantidad:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {cantidadLabel}
                  </Typography>
                </Grid>

                {showDunCode && (
                  <Fragment>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        DUN:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {sharedState.dunCode}
                      </Typography>
                    </Grid>
                  </Fragment>
                )}

                {sharedState.selectedBatch && (
                  <Fragment>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Lote:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {sharedState.selectedBatch.batchNumber}
                      </Typography>
                    </Grid>
                  </Fragment>
                )}

                {sharedState.isDamaged && (
                  <Fragment>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Estado:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Chip
                        label="Requiere Calidad"
                        size="small"
                        style={{ backgroundColor: '#ffab00', color: 'white' }}
                      />
                    </Grid>
                  </Fragment>
                )}

                {sharedState.documentNumber && (
                  <Fragment>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Documento:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {sharedState.documentNumber}
                      </Typography>
                    </Grid>
                  </Fragment>
                )}

                {sharedState.observations && (
                  <Fragment>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                        Observaciones:
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: 4 }}>
                        {sharedState.observations}
                      </Typography>
                    </Grid>
                  </Fragment>
                )}
              </Grid>
            </Box>

            <Divider style={{ margin: '20px 0' }} />

            {/* Fecha y hora */}
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
              {new Date().toLocaleString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>

            {/* Countdown */}
            <Typography variant="body1" style={{ marginBottom: 16, color: '#666' }}>
              Regresando al inicio en {countdown} segundos...
            </Typography>

            {/* Botón manual */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleReturnToStart}
              size="large"
              style={{
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                padding: '12px 32px'
              }}
            >
              Volver al Inicio
            </Button>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box
      style={{
        height: getStepperContainerHeight(),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        overflow: 'auto',
        padding: isMobile ? '8px 0' : '16px 0'
      }}
    >
      <Box
        style={{
          width: '90%',
          maxWidth: 600,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Card
          style={{
            width: '100%',
            padding: 24,
            textAlign: 'center',
            border: `2px solid ${theme.palette.primary.main}`,
            borderRadius: 12,
            backgroundColor: theme.palette.background.paper
          }}
        >
          <CardContent>
            {/* Título principal */}
            <Typography variant='h4' style={{ color: theme.palette.primary.main, marginBottom: 16, fontWeight: 'bold' }}>
              Confirmar Reubicación
            </Typography>

            <Divider style={{ margin: '20px 0' }} />

            {/* Información del producto */}
            {productChips}

            {/* Detalles del movimiento estilo recibo */}
            <Box style={{ textAlign: 'left', marginBottom: 20 }}>

              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Origen:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {sharedState.posicionOrigen}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Destino:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {sharedState.posicionDestino}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Cantidad:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {cantidadLabel}
                  </Typography>
                </Grid>

                {showDunCode && (
                  <Fragment>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        DUN:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {sharedState.dunCode}
                      </Typography>
                    </Grid>
                  </Fragment>
                )}

                {sharedState.selectedBatch && (
                  <Fragment>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Lote:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {sharedState.selectedBatch.batchNumber}
                      </Typography>
                    </Grid>
                  </Fragment>
                )}

                {sharedState.isDamaged && (
                  <Fragment>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Estado:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Chip
                        label="Requiere Calidad"
                        size="small"
                        style={{ backgroundColor: '#ffab00', color: 'white' }}
                      />
                    </Grid>
                  </Fragment>
                )}

                {sharedState.documentNumber && (
                  <Fragment>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Documento:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {sharedState.documentNumber}
                      </Typography>
                    </Grid>
                  </Fragment>
                )}

                {sharedState.observations && (
                  <Fragment>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                        Observaciones:
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: 4 }}>
                        {sharedState.observations}
                      </Typography>
                    </Grid>
                  </Fragment>
                )}
              </Grid>
            </Box>

            <Divider style={{ margin: '20px 0' }} />

            {/* Botones de acción */}
            <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="center" style={{ marginTop: 20 }}>
              <Button
                variant="outlined"
                onClick={prev}
                size="large"
                style={{
                  marginRight: isMobile ? 0 : 16,
                  marginBottom: isMobile ? 12 : 0,
                  minHeight: '48px',
                  minWidth: isMobile ? '100%' : '120px'
                }}
              >
                Anterior
              </Button>

              <LoadingButton
                loading={isExecuting}
                variant="contained"
                color="primary"
                onClick={handleExecuteRelocate}
                disabled={!canExecute}
                size="large"
                style={{
                  backgroundColor: canExecute ? theme.palette.success.main : theme.palette.grey[400],
                  color: 'white',
                  minHeight: '48px',
                  minWidth: isMobile ? '100%' : '200px',
                  padding: '12px 32px'
                }}
              >
                {canExecute ? 'Confirmar Reubicación' : 'Complete la información requerida'}
              </LoadingButton>
            </Box>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <Box mt={3} style={{ width: '100%' }}>
            <Paper
              style={{
                backgroundColor: theme.palette.error.light + '20',
                border: `1px solid ${theme.palette.error.main}`,
                padding: '12px 16px',
                borderRadius: '4px',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" style={{ color: theme.palette.error.dark }}>
                {error}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Warning for required fields */}
        {sharedState.requiresComments && !canExecute && !error && (
          <Box mt={3} style={{ width: '100%' }}>
            <Paper
              style={{
                backgroundColor: theme.palette.warning.light + '20',
                border: `1px solid ${theme.palette.warning.main}`,
                padding: '12px 16px',
                borderRadius: '4px',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" style={{ color: theme.palette.warning.dark }}>
                Complete el número de documento y las observaciones para continuar
              </Typography>
            </Paper>
          </Box>
        )}

      </Box>
    </Box>
  )
}

